/**
 * ui — Design system and shared components.
 * Base styles live in the global stylesheet (index.css); newer primitives
 * co-locate their CSS as ui/*.css, using tokens only.
 */

// ─── Button ──────────────────────────────────────────────────────────────────
import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
}

export function Button({ variant = 'primary', size = 'md', isLoading, children, disabled, className = '', ...props }: ButtonProps) {
    return (
        <button
            {...props}
            disabled={disabled || isLoading}
            className={`btn btn-${variant} btn-${size} ${isLoading ? 'btn-loading' : ''} ${className}`}
        >
            {isLoading ? <span className="spinner-sm" /> : children}
        </button>
    );
}

// ─── Input ───────────────────────────────────────────────────────────────────
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
}

export function Input({ label, error, id, className = '', ...props }: InputProps) {
    return (
        <div className="form-group">
            {label && <label htmlFor={id} className="form-label">{label}</label>}
            <input id={id} className={`form-input ${error ? 'form-input-error' : ''} ${className}`} {...props} />
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

// ─── Badge ───────────────────────────────────────────────────────────────────
interface BadgeProps {
    children: React.ReactNode;
    variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
}

export function Badge({ children, variant = 'neutral' }: BadgeProps) {
    return <span className={`badge badge-${variant}`}>{children}</span>;
}

// ─── Spinner ─────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
    return <div className={`spinner spinner-${size}`} aria-label="Loading" />;
}

// ─── Card ────────────────────────────────────────────────────────────────────
export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
    return <div className={`card ${className}`}>{children}</div>;
}

// ─── PageHeader ──────────────────────────────────────────────────────────────
export { PageHeader } from './PageHeader';
export type { Crumb } from './PageHeader';

// ─── Table ───────────────────────────────────────────────────────────────────
export { Table } from './Table';
export type { Column, TableProps } from './Table';

// ─── Redesign primitives (U11) ───────────────────────────────────────────────
export { Dialog, Drawer } from './Dialog';
export type { DialogProps, DrawerProps } from './Dialog';
export { Tabs, ButtonTabs, TabPanel } from './Tabs';
export type { TabLinkItem, ButtonTabItem } from './Tabs';
export { StateView } from './StateView';
export type { StateViewProps } from './StateView';
